// A device copy of one database section (an IndexedDB store), kept current through the change
// index (changeFeed.js; how it works: docs/change-index.md). A page asks for the whole section
// with all(): the first call on a page catches up (fetching only the records changed since the
// device last did), later calls read the copy. onChange() starts the one watch this page keeps
// on that section. Reading or listening to the section itself downloaded all of it every time.
//
//   const invoices = deviceCopy('invoices');
//   const all = await invoices.all();          // { id: record }
//   const off = invoices.onChange(id => ...);  // after someone's change (id, or null for many)
import { ref, get } from 'firebase/database';
import { database, isFirebaseConnected } from './initFirebase';
import { getDB } from './utilities';
import { catchUp, watch } from './changeFeed';

// The section path → its IndexedDB store. Each section needs a noteChange() trigger in
// functions/changeFeed.js, and its store in utilities.js. The store's `id` is the record's key;
// keepId: the records carry their own `id` field too (= the key), so it stays on them.
const SECTIONS = {
  customers: { store: 'customerData' },
  closedJobs: { store: 'closedJobs' },
  invoices: { store: 'invoiceData', keepId: true },
  payments: { store: 'paymentData', keepId: true },
  inventory: { store: 'inventoryData', keepId: true },
  inventoryPOs: { store: 'inventoryPOs', keepId: true },
  'mooringDatabase/tags': { store: 'mooringDatabase' },
  'batteries/units': { store: 'batteryUnits' },
  'batteries/readings': { store: 'batteryReadings' },
};

async function tx(store, mode, fn) {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const out = fn(t.objectStore(store));
    t.oncomplete = () => resolve(out && 'result' in out ? out.result : undefined);
    t.onerror = e => reject(e.target?.error || e);
    t.onabort = e => reject(e.target?.error || e);
  });
}

const copies = {};

/** Whether `root` is on the change index with its copy in `store`. */
export const hasCopy = (root, store) => SECTIONS[root]?.store === store;

/** The device copy of `root` (one per section per page). */
export function deviceCopy(root) {
  if (copies[root]) return copies[root];
  const { store, keepId } = SECTIONS[root] || {};
  if (!store) throw new Error(`deviceCopy: no store for ${root}`);
  const strip = keepId ? r => ({ ...r }) : ({ id: _id, ...rest }) => rest;
  const listeners = new Set();
  let ready = null;
  let watching = false;

  const put = (id, val) => tx(store, 'readwrite', st => {
    if (val && typeof val === 'object') st.put({ ...val, id });
    else st.delete(id);
  });
  const full = async () => {
    const v = (await get(ref(database, root))).val() || {};
    await tx(store, 'readwrite', st => {
      st.clear();
      Object.entries(v).forEach(([id, val]) => { if (id !== 'lastUpdated' && val && typeof val === 'object') st.put({ ...val, id }); });
    });
  };
  const notify = id => listeners.forEach(fn => { try { fn(id); } catch (err) { console.error(`[deviceCopy] ${root} listener failed`, err); } });

  // Once a page. Offline: the copy as it is, and the next call tries again.
  const start = () => {
    if (ready) return ready;
    if (!isFirebaseConnected()) return Promise.resolve();
    ready = catchUp({ root, store, full }).catch(err => { ready = null; throw err; });
    return ready;
  };
  const startWatch = () => {
    if (watching) return;
    watching = true;
    start().catch(() => {}).finally(() => watch({
      root, store,
      onChange: async (id, val) => { await put(id, val); notify(id); },
    }));
  };

  const copy = {
    /** Brings the copy up to date (once a page; later calls are free). */
    sync: () => start(),
    /** Every record, { id: record }, caught up first when online. */
    async all() {
      await start();
      const rows = await tx(store, 'readonly', st => st.getAll());
      return Object.fromEntries((rows || []).map(r => [r.id, strip(r)]));
    },
    /** One record from the copy (null if it has none). */
    async one(id) {
      await start();
      const r = await tx(store, 'readonly', st => st.get(id));
      return r ? strip(r) : null;
    },
    /** Calls fn(id) after each change from anyone (this page's watch on the section). */
    onChange(fn) {
      listeners.add(fn);
      startWatch();
      return () => listeners.delete(fn);
    },
    /** This page's own save, into the copy at once (the feed brings the same record later). */
    async noteWrite(id, patch, { replace = false } = {}) {
      try {
        const cur = replace ? {} : strip((await tx(store, 'readonly', st => st.get(id))) || { id });
        Object.entries(patch).forEach(([path, v]) => {
          const keys = path.split('/');
          let o = cur;
          keys.slice(0, -1).forEach(k => { o = o[k] = (o[k] && typeof o[k] === 'object') ? o[k] : {}; });
          if (v === null) delete o[keys[keys.length - 1]]; else o[keys[keys.length - 1]] = v;
        });
        await put(id, Object.keys(cur).length ? cur : null);
        notify(id);
      } catch (err) {
        console.error(`[deviceCopy] ${root} local write failed`, err);
      }
    },
  };
  copies[root] = copy;
  return copy;
}
